import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Switch,
  Typography,
  Alert
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { fetchWithAuth } from '../../api/backendApi';
import { useAuth } from '../../contexts/AuthContext';
import MainLayout from '../../components/layout/MainLayout';

const SEVERITY_COLORS = {
  high: 'error',
  med: 'warning',
  low: 'info'
};

const IncidentTypeListPage = () => {
  const navigate = useNavigate();
  const { userData } = useAuth();
  
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const tenantId = userData?.tenantId;
  const role = userData?.role;
  const hasAdminAccess = ['admin', 'superadmin', 'tenant_admin', 'overseer'].includes(role);

  const loadIncidentTypes = useCallback(async () => {
    if (!tenantId) {
      setLoading(false);
      return;
    }

    if (!hasAdminAccess) {
      setError('You do not have permission to access this page');
      setLoading(false);
      return;
    }

    try {
      console.log('[INCIDENT-TYPES] Loading incident types for tenant:', tenantId);
      
      const response = await fetchWithAuth(
        `/api/v2/tenants/${tenantId}/incident-types`,
        'GET'
      );

      if (response?.incidentTypes) {
        console.log('[INCIDENT-TYPES] Loaded incident types:', response.incidentTypes.length);
        setIncidentTypes(response.incidentTypes);
        setError(null);
      } else {
        setIncidentTypes([]);
      }
    } catch (err) {
      console.error('[INCIDENT-TYPES] Error loading incident types:', err);
      setError(err.message || 'Failed to load incident types');
    } finally {
      setLoading(false);
    }
  }, [tenantId, hasAdminAccess]);

  useEffect(() => {
    loadIncidentTypes();
  }, [loadIncidentTypes]);

  const handleToggleActive = async (typeKey, currentActive) => {
    try {
      await fetchWithAuth(
        `/api/v2/tenants/${tenantId}/incident-types/${typeKey}`,
        'PUT',
        {
          active: !currentActive
        }
      );
      console.log('[INCIDENT-TYPES] Toggled active status:', typeKey);
      
      // Reload the list
      loadIncidentTypes();
    } catch (err) {
      console.error('[INCIDENT-TYPES] Error toggling active status:', err);
      setError(`Failed to update: ${err.message}`);
    }
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 200
    },
    {
      field: 'key',
      headerName: 'Key',
      flex: 1,
      minWidth: 150
    },
    {
      field: 'severity',
      headerName: 'Severity',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value?.toUpperCase() || 'NONE'}
          color={SEVERITY_COLORS[params.value] || 'default'}
          size="small"
        />
      )
    },
    {
      field: 'active',
      headerName: 'Active',
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.value || false}
          onChange={() => handleToggleActive(params.row.key, params.value)}
          size="small"
        />
      )
    },
    {
      field: 'buttons',
      headerName: 'Buttons',
      width: 100,
      valueGetter: (...args) => {
        const first = args[0];
        const second = args[1];
        const row = first?.row || second || null;
        return row?.buttons?.length || 0;
      }
    },
    {
      field: 'guardRules',
      headerName: 'Guard',
      width: 100,
      renderCell: (params) => {
        const hasGuard = params.value?.onePerSite || params.value?.onePerTenant;
        return hasGuard ? (
          <Chip label="Yes" color="primary" size="small" />
        ) : (
          <Chip label="No" variant="outlined" size="small" />
        );
      }
    },
    {
      field: 'updatedAt',
      headerName: 'Last Updated',
      width: 180,
      valueGetter: (...args) => {
        const first = args[0];
        const value = first?.value ?? args[0];
        if (!value) return 'Never';
        const date = value.toDate ? value.toDate() : new Date(value);
        return date.toLocaleString();
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => navigate(`/admin/incidents/${params.row.key}`)}
            title="Edit"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => navigate(`/admin/incidents/${params.row.key}?view=true`)}
            title="View"
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  if (!hasAdminAccess) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          You do not have permission to access this page. Admin access required.
        </Alert>
      </Box>
    );
  }

  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Incident Types
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure incident types, panel buttons, and default assignments
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/incidents/new')}
          size="large"
        >
          Create Incident Type
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Data Grid */}
      <Card>
        <CardContent>
          <DataGrid
            rows={incidentTypes}
            columns={columns}
            loading={loading}
            autoHeight
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
              sorting: { sortModel: [{ field: 'name', sort: 'asc' }] }
            }}
            disableRowSelectionOnClick
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none'
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover'
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Empty State */}
      {!loading && incidentTypes.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            No incident types configured
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first incident type to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/incidents/new')}
          >
            Create Incident Type
          </Button>
        </Box>
      )}
      </Box>
    </MainLayout>
  );
};

export default IncidentTypeListPage;
